import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  QueryHookOptions,
  QueryResult as ApolloQueryResult,
} from '@apollo/client'

export type UsePaginationOptions<
  Query,
  QueryVariables,
  QueryResult extends ApolloQueryResult
> = {
  // 1ページごとのサイズ
  initialSize?: number
  // クエリ関数
  queryOptions?: QueryHookOptions<Query, QueryVariables>
  queryHooks: (options?: QueryHookOptions<Query, QueryVariables>) => QueryResult
}

export interface UsePaginationResult<QueryResult extends ApolloQueryResult>
  extends Pick<QueryResult, 'data' | 'error' | 'loading' | 'refetch'> {
  // 1ページごとのサイズ
  size: number
  // 現在のページ番号（0 始まり）
  page: number
  // 総件数
  total: number
  // 一つ前のページに戻る関数
  prev: () => void
  // 次のページへ移動する関数
  next: () => void
  // 指定されたページ番号へ移動する関数
  goto: (n: number) => void
  // ページサイズ更新関数
  paginate: (page: number, size: number) => void
}

const CURSOR_PREFIX = 'arrayconnection:'
const DEFAULT_SIZE = 10
const INITIAL_PAGE = 1

function offsetToCursor(offset: number): string {
  return btoa(`${CURSOR_PREFIX}${offset}`)
}

function calcMaxPage(count: number, size: number): number {
  return Math.ceil(count / size)
}

/**
 * ページネーションを実現するための React Hooks 関数
 * query の variables に渡されたページネーションに関する値は上書きされる
 * @param options
 */
export function usePagination<
  Query,
  QueryVariables,
  QueryResult extends ApolloQueryResult
>(
  options: UsePaginationOptions<Query, QueryVariables, QueryResult>
): UsePaginationResult<QueryResult> {
  // 1ページごとの表示件数
  const [size, setSize] = useState<number>(options.initialSize || DEFAULT_SIZE)
  // 現在のページ番号
  const [page, setPage] = useState<number>(INITIAL_PAGE)
  // GraphQL Query
  const {
    data,
    error,
    loading: initialLoading,
    refetch: internalRefetch,
    variables,
  } = options.queryHooks({
    ...options.queryOptions,
    variables: {
      ...options.queryOptions.variables,
      first: size,
      last: null,
      before: null,
      after: null,
    },
  })
  // 総件数
  const total = useMemo<number>(() => {
    let totalCount = 0
    if (typeof data === 'undefined') return totalCount

    Object.entries(data).forEach(([_, value1]) => {
      Object.entries(value1).forEach(([key2, value2]) => {
        if (key2 === 'totalCount') {
          totalCount = value2
        }
      })
    })
    return totalCount
  }, [data])
  // 最大ページ数
  const maxPage = useMemo<number>(() => calcMaxPage(total, size), [total, size])
  // ロード中かどうか
  const [loading, setLoading] = useState(initialLoading)

  useEffect(() => setLoading(initialLoading), [initialLoading])

  // サイズとページが変わるたびに再取得
  useEffect(() => {
    // 1 ページ目でオフセットを計算してしまうと API サーバーから返ってくる件数が一つ少なくなってしまう
    // 0 番目を after で指定 -> オフセット１として計算されるため

    if (typeof error === 'undefined' && !loading) {
      const cursor = page > 1 ? offsetToCursor((page - 1) * size - 1) : null
      setLoading(true)
      internalRefetch({
        ...variables,
        first: size,
        last: null,
        before: null,
        after: cursor,
      }).finally(() => setLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size, page, internalRefetch]) // deps からは variables は除外 (variables の値が変わるのは検索条件が変わるとき。)

  const refetch = useCallback(
    async (newVariables: QueryVariables) => {
      // この関数が呼ばれるのは基本的に絞り込み条件が変わる時
      // 最初のページを取得するために after と before、last を削除
      return await internalRefetch({
        ...variables,
        ...newVariables,
        last: null,
        before: null,
        after: null,
      })
    },
    [internalRefetch, variables]
  )

  const prev = useCallback(() => {
    // すでにページが 0 ページ目の場合なにもしない
    setPage((prevState) => (prevState > 0 ? prevState - 1 : prevState))
  }, [setPage])

  const next = useCallback(() => {
    setPage((prevState) => (prevState < maxPage ? prevState + 1 : prevState))
  }, [maxPage])

  const goto = useCallback(
    (n: number) => {
      // maxPage より大きい値を指定された場合、maxPage に遷移する
      if (n > maxPage) n = maxPage
      setPage(n)
    },
    [maxPage]
  )

  const paginate = useCallback((page: number, size: number) => {
    setPage(page)
    setSize(size)
  }, [])

  return {
    data,
    loading,
    error,
    refetch,
    size,
    page,
    total,
    prev,
    next,
    goto,
    paginate,
  }
}
